import { useState } from "react";
import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { faCheckDouble } from "@fortawesome/pro-solid-svg-icons";

import { UISubscriptionStatus } from "common/types";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import CrossedPrice from "client/common/components/content/CrossedPrice";
import QuantityInput from "client/common/components/inputs/QuantityInput";
import Button from "client/common/components/buttons/Button";
import { useCartItemAddMutation } from "client/carts/hooks";
import { CartCode } from "client/carts/types";
import { urls } from "client/routes";
import { sendAddToCart, sendSelectItem } from "client/analytics/utils";

import { ClientSubscription, ListSubscription } from "../types";

import styles from "../styles/SubscriptionItem.module.less";

type SubscriptionItemProps = {
  subscription: ClientSubscription | ListSubscription;
};

const SubscriptionItem = (props: SubscriptionItemProps) => {
  const { subscription } = props;

  // We may want to add a toast on error here.
  const { mutate: addCartItem, isLoading: isAddingToCart } = useCartItemAddMutation({
    shouldOpenCart: true,
    onSuccess: (_, quantity) => {
      sendAddToCart(
        subscription.item,
        quantity,
        subscription.next_order_data.subtotal - subscription.next_order_data.discount,
        subscription.next_order_data.discount,
      );
    },
  });

  const [quantity, setQuantity] = useState(subscription.quantity);

  const itemUrl = urls.products.detail({
    params: { id: subscription.item.product.product_line.id },
    search: {
      item: subscription.item.id,
      product: subscription.item.product.id,
    },
  });

  const addToCart = () => {
    addCartItem({
      item: subscription.item.id,
      quantity,
      cart_id: CartCode.ACTIVE,
      flavor_selection: subscription.flavor_selection || undefined,
    });
  };
  const isSubscriptionActive = subscription.visible_status === UISubscriptionStatus.Active;

  const renderPrescriptionChip = () => {
    if (!subscription.has_prescription) {
      return (
        <>
          <Icon icon={faExclamationTriangle} /> Prescription needs approval
        </>
      );
    }

    if (subscription.prescription_needs_renewal_for_next_order) {
      return (
        <>
          <Icon icon={faExclamationTriangle} /> Prescription needs renewal
        </>
      );
    }

    return (
      <>
        <Icon icon={faCheckDouble} /> Active prescription on file
      </>
    );
  };

  return (
    <div className={styles.item}>
      <CatalogImage.Link
        dest={itemUrl}
        image={subscription.item.image}
        size="medium"
        alt={subscription.item.full_name}
        className={styles.image}
        onClick={() =>
          sendSelectItem(subscription.item, "Subscriptons", subscription.next_order_data.unit_price)
        }
      />
      <div className={styles.content}>
        <div>
          <LinkButton
            dest={itemUrl}
            className={styles.name}
            onClick={() =>
              sendSelectItem(
                subscription.item,
                "Subscriptons",
                subscription.next_order_data.unit_price,
              )
            }
          >
            {subscription.item.product.product_line.name}
          </LinkButton>
          <p className={styles.description}>
            {subscription.item.product.name} · {subscription.item.name}
            {subscription.flavors_display && ` · ${subscription.flavors_display}`} · Qty{" "}
            {subscription.quantity}
          </p>
        </div>
        {subscription.item.product.product_line.prescription_required && (
          <TextChip>{renderPrescriptionChip()}</TextChip>
        )}
        <CrossedPrice
          crossed={subscription.next_order_data.subtotal}
          price={subscription.next_order_data.subtotal - subscription.next_order_data.discount}
        />
        <p className={styles.info}>{subscription.item.product.product_line.short_description}</p>
        {!isSubscriptionActive && (
          <div className={styles.reorder}>
            <QuantityInput value={quantity} onChange={setQuantity} min={1} />
            <Button className={styles.button} isLoading={isAddingToCart} onClick={addToCart}>
              Reorder Item
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionItem;
